import { MessageTemplatesCard } from '../../components/MessageTemplatesCard';
import { HESAP_TEMPLATES } from '../../lib/messageTemplates';
import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Lock, MapPin, Download, Loader2, FileSpreadsheet, Eye, EyeOff, ShieldCheck, CreditCard, CalendarCheck, AlertTriangle, CheckCircle2, Crown } from 'lucide-react';
import { TR_CITIES } from '../../lib/trCities';
import { GoogleLinkCard } from '../../components/auth/GoogleLinkCard';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

export default function HesapSettingsPage() {
  const { token, user } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Şifre değişimi
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error('Yeni şifre en az 6 karakter olmalı'); return; }
    if (newPw !== newPw2) { toast.error('Yeni şifreler eşleşmiyor'); return; }
    setPwLoading(true);
    try {
      const r = await fetch(`${API}/auth/change-password`, {
        method: 'PUT', headers,
        body: JSON.stringify({ current_password: curPw, new_password: newPw })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Hata');
      toast.success('Şifre başarıyla değiştirildi');
      setCurPw(''); setNewPw(''); setNewPw2('');
    } catch (e) { toast.error(e.message); }
    finally { setPwLoading(false); }
  };

  // Konum / hava durumu
  const [selectedCity, setSelectedCity] = useState(user?.city || '');
  const [cityLoading, setCityLoading] = useState(false);

  const saveCity = async (cityName) => {
    const city = TR_CITIES.find(c => c.name === cityName);
    setCityLoading(true);
    try {
      const r = await fetch(`${API}/auth/me/location`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          city: city?.name || '',
          latitude: city?.lat || null,
          longitude: city?.lon || null,
        })
      });
      if (!r.ok) throw new Error('Hata');
      // local user'ı güncelle
      const stored = JSON.parse(localStorage.getItem('hesap_user') || 'null');
      if (stored) {
        stored.city = city?.name || '';
        stored.latitude = city?.lat || null;
        stored.longitude = city?.lon || null;
        localStorage.setItem('hesap_user', JSON.stringify(stored));
        useHesapAuth.setState({ user: stored });
      }
      setSelectedCity(cityName);
      toast.success(cityName ? `${cityName} konumu kaydedildi` : 'Konum kaldırıldı');
    } catch { toast.error('Konum kaydedilemedi'); }
    finally { setCityLoading(false); }
  };

  // Excel yedek
  const downloadBackup = () => {
    const a = document.createElement('a');
    a.href = `${API}/backup-excel?t=${encodeURIComponent(token)}`;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 1000);
    toast.success('Excel yedeği indiriliyor — tarayıcının indirme bölümünden takip edebilirsiniz');
  };

  const userPerms = user?.permissions || [];
  const isAdmin = user?.role === 'admin';
  const has = (p) => isAdmin || userPerms.includes(p);
  const backupModules = [
    { perm: 'urunler', label: 'Ürünler' },
    { perm: 'musteriler', label: 'Müşteriler' },
    { perm: 'pos', label: 'Satışlar' },
    { perm: 'gelir_gider', label: 'Gelir / Gider' },
    { perm: 'musteriler', label: 'Veresiye İşlemleri' },
    { perm: 'is_takibi', label: 'İş Takibi' },
    { perm: 'teknik_servis', label: 'Teknik Servis' },
    { perm: 'apartman', label: 'Apartman Daireleri' },
  ].filter(m => has(m.perm));

  // Abonelik bilgisi & planlar
  const [mySub, setMySub] = useState(null);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    fetch(`${API}/subscription/me`, { headers }).then(r => r.json()).then(setMySub).catch(() => {});
    fetch(`${API}/subscription/plans`).then(r => r.json()).then(setPlans).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="hesap-settings-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-sky-600 text-white shadow-md">
          <SettingsIcon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Kullanıcı Ayarları</h1>
          <p className="text-sm text-slate-500">Hesap güvenliği, konum ve veri yedekleme</p>
        </div>
      </div>

      <GoogleLinkCard apiBase="/api/hesap/auth" token={token} />

      <MessageTemplatesCard path="/api/hesap" token={token} defaults={HESAP_TEMPLATES} />

      {/* Hesap Bilgileri */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Hesap Bilgileri
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <Label className="text-xs text-slate-500">Ad Soyad</Label>
            <div className="font-bold">{user?.full_name}</div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">E-posta</Label>
            <div className="font-mono text-xs">{user?.email}</div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Rol</Label>
            <div className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded font-bold">
              {user?.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Telefon</Label>
            <div>{user?.phone || '—'}</div>
          </div>
        </CardContent>
      </Card>

      {/* Şifre Değişimi */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-600" />
            Şifre Değiştir
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-3" data-testid="password-change-form">
            <div>
              <Label className="text-xs">Mevcut Şifre</Label>
              <div className="relative">
                <Input type={showCur ? 'text' : 'password'} value={curPw}
                  onChange={e => setCurPw(e.target.value)} required
                  data-testid="cur-pw-input" />
                <button type="button" onClick={() => setShowCur(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {showCur ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Yeni Şifre</Label>
                <div className="relative">
                  <Input type={showNew ? 'text' : 'password'} value={newPw}
                    onChange={e => setNewPw(e.target.value)} required minLength={6}
                    data-testid="new-pw-input" />
                  <button type="button" onClick={() => setShowNew(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Yeni Şifre (Tekrar)</Label>
                <Input type={showNew ? 'text' : 'password'} value={newPw2}
                  onChange={e => setNewPw2(e.target.value)} required minLength={6}
                  data-testid="new-pw2-input" />
              </div>
            </div>
            <Button type="submit" disabled={pwLoading}
              className="bg-amber-600 hover:bg-amber-700" data-testid="save-password-btn">
              {pwLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              Şifreyi Değiştir
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Konum & Hava Durumu */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4 text-sky-600" />
            Konum & Hava Durumu
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            Şehir seçtiğinizde üst barda anlık sıcaklık + hava durumu gösterilir. Bu bilgi sadece sizin için saklanır.
          </p>
          <div className="flex gap-2">
            <select value={selectedCity} onChange={e => saveCity(e.target.value)}
              disabled={cityLoading}
              className="flex-1 border rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:border-sky-500"
              data-testid="city-select">
              <option value="">— Şehir seçin —</option>
              {TR_CITIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            {cityLoading && <Loader2 className="h-5 w-5 animate-spin text-sky-500 mt-2.5" />}
          </div>
          {user?.city && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" />
              Aktif konum: <b>{user.city}</b>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Excel Yedek */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-green-600" />
            Excel Yedek
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            Sahip olduğunuz yetkilere göre tüm verileriniz tek bir <b>.xlsx</b> dosyasına aktarılır.
            Her modül ayrı sayfa olarak gelir.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {backupModules.length === 0 ? (
              <span className="text-xs text-slate-400">Yedeklenecek modül yok</span>
            ) : backupModules.map((m, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded border border-slate-200">
                {m.label}
              </span>
            ))}
          </div>
          <Button onClick={downloadBackup} disabled={backupModules.length === 0}
            className="bg-green-600 hover:bg-green-700 text-white" data-testid="download-backup-btn">
            <Download className="h-4 w-4 mr-2" />
            Excel Yedeği İndir
          </Button>
        </CardContent>
      </Card>

      {/* Abonelik */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-violet-600" />
            Abonelik Bilgileri
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Mevcut durum */}
          {mySub === null ? (
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />Yükleniyor...
            </div>
          ) : mySub.active ? (
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-300 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-600 rounded-lg shadow-md">
                    <Crown className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-emerald-700 font-medium">Aktif Plan</p>
                    <p className="text-lg font-bold text-emerald-900">{mySub.plan?.name || 'Plan'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-emerald-900">{mySub.days_left}</p>
                  <p className="text-[10px] text-emerald-700 uppercase font-bold tracking-wider">gün kaldı</p>
                </div>
              </div>
              {mySub.end_at && (
                <p className="text-[11px] text-emerald-700 mt-3 flex items-center gap-1">
                  <CalendarCheck className="h-3 w-3" />
                  Bitiş: {new Date(mySub.end_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                </p>
              )}
            </div>
          ) : mySub.expired ? (
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-red-900">Aboneliğiniz sona erdi</p>
                <p className="text-xs text-red-700">Yöneticinizle iletişime geçerek aboneliğinizi yenileyin.</p>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-amber-900">Aktif abonelik bulunmuyor</p>
                <p className="text-xs text-amber-700">Bir plan seçmek için yöneticinizle iletişime geçin.</p>
              </div>
            </div>
          )}

          {/* Plan listesi */}
          <div>
            <p className="text-xs text-slate-500 mb-2 font-medium">Mevcut Planlar</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {plans.map(p => {
                const isMine = mySub?.plan_id === p.id;
                return (
                  <div key={p.id}
                    className={`relative p-3 rounded-lg border-2 transition ${
                      isMine ? 'border-emerald-400 bg-emerald-50/40'
                             : 'border-slate-200 hover:border-violet-300 bg-white'
                    }`}>
                    {isMine && (
                      <span className="absolute -top-2 right-2 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />Aktif
                      </span>
                    )}
                    <p className="font-bold text-sm">{p.name}</p>
                    <p className="text-xl font-black text-violet-700 mt-1">
                      {p.price === 0 ? 'Ücretsiz' : `${p.price} ${p.currency || 'TL'}`}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{p.duration_days} gün</p>
                  </div>
                );
              })}
            </div>
            {!isAdmin && (
              <p className="text-[10px] text-slate-400 mt-2">
                Plan değişikliği için yöneticinizle iletişime geçin.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
